/**
 * CtrlScrollZoom
 *
 * Drop this child inside a react-leaflet `<MapContainer>` to make the map
 * zoom only when ctrl (or cmd on mac) is held during a wheel event. Wheel
 * events without the modifier pass through to the browser so the page
 * scrolls instead of the map zooming — the standard "doesn't hijack scroll"
 * UX users expect from Google Maps embeds.
 *
 * The parent MapContainer should set `scrollWheelZoom={false}` so Leaflet's
 * default scroll-zoom is off; this component handles the ctrl-modified case
 * manually via the underlying map instance.
 */
import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

export function CtrlScrollZoom() {
  const map = useMap();

  useEffect(() => {
    // Ensure Leaflet's own scroll-zoom is off — we drive zoom manually.
    map.scrollWheelZoom.disable();

    const container = map.getContainer();
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;       // page scrolls instead
      e.preventDefault();
      const step = e.deltaY > 0 ? -1 : 1;
      map.setZoomAround(map.mouseEventToLatLng(e), map.getZoom() + step);
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [map]);

  return null;
}
